import { useState } from 'react';
import type { IdeaTask, TaskStatus, Priority } from '@/lib/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { BlockedReasonModal, DoneNoteModal } from './TaskModals';
import { 
  AlertCircle,
  CheckCircle2,
  Circle,
  Clock,
  FlaskConical,
  Hammer,
  Search,
  Users,
  FileText,
  Trash2,
  ArrowUpDown,
  Sparkles,
  Pencil,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { toast } from 'sonner';

interface PlanListProps {
  tasks: IdeaTask[];
  onUpdateTask: (taskId: string, updates: Partial<IdeaTask>) => Promise<boolean>;
  onDeleteTask: (taskId: string) => Promise<boolean>;
  onEditTask?: (task: IdeaTask) => void;
}

type SortField = 'due_date' | 'priority' | 'task_type' | 'status';
type SortDir = 'asc' | 'desc';

const STATUS_CONFIG: Record<TaskStatus, { label: string; icon: typeof Circle; className: string }> = {
  next: { label: 'Next', icon: Circle, className: 'text-muted-foreground' },
  doing: { label: 'Doing', icon: Clock, className: 'text-primary' },
  blocked: { label: 'Blocked', icon: AlertCircle, className: 'text-destructive' },
  done: { label: 'Done', icon: CheckCircle2, className: 'text-primary' },
};

const TYPE_ICONS: Record<string, typeof FlaskConical> = {
  experiment: FlaskConical,
  build: Hammer,
  research: Search,
  outreach: Users,
  admin: FileText,
};

const PRIORITY_ORDER: Record<Priority, number> = { high: 0, medium: 1, low: 2 };
const STATUS_ORDER: Record<TaskStatus, number> = { doing: 0, next: 1, blocked: 2, done: 3 };

const WIP_LIMIT = 3;

export function PlanList({ tasks, onUpdateTask, onDeleteTask, onEditTask }: PlanListProps) {
  const [sortField, setSortField] = useState<SortField>('status');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [pendingMove, setPendingMove] = useState<{ task: IdeaTask; targetStatus: TaskStatus } | null>(null);
  const [showBlockedModal, setShowBlockedModal] = useState(false);
  const [showDoneModal, setShowDoneModal] = useState(false);

  const doingCount = tasks.filter(t => t.status === 'doing').length;

  const sortedTasks = [...tasks].sort((a, b) => {
    let cmp = 0;
    switch (sortField) {
      case 'due_date':
        const aDate = a.due_date ? new Date(a.due_date).getTime() : Infinity;
        const bDate = b.due_date ? new Date(b.due_date).getTime() : Infinity;
        cmp = aDate - bDate;
        break;
      case 'priority':
        cmp = PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority];
        break;
      case 'task_type':
        cmp = a.task_type.localeCompare(b.task_type);
        break;
      case 'status':
        cmp = STATUS_ORDER[a.status] - STATUS_ORDER[b.status];
        break;
    }
    return sortDir === 'desc' ? -cmp : cmp;
  });

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const handleStatusChange = async (task: IdeaTask, newStatus: TaskStatus) => {
    if (newStatus === task.status) return;

    if (newStatus === 'doing' && task.status !== 'doing' && doingCount >= WIP_LIMIT) {
      toast.error('Finish something. Then start something.', {
        description: `WIP limit: max ${WIP_LIMIT} tasks in Doing`,
      });
      return;
    }

    if (newStatus === 'blocked') {
      setPendingMove({ task, targetStatus: newStatus });
      setShowBlockedModal(true);
      return;
    }

    if (newStatus === 'done') {
      setPendingMove({ task, targetStatus: newStatus });
      setShowDoneModal(true);
      return;
    }

    await onUpdateTask(task.id, { status: newStatus });
  };

  const handleBlockedConfirm = async (reason: string) => {
    if (!pendingMove) return;
    await onUpdateTask(pendingMove.task.id, { status: 'blocked', blocked_reason: reason });
    setPendingMove(null);
  };

  const handleDoneConfirm = async (note: string, evidence: string) => {
    if (!pendingMove) return;
    await onUpdateTask(pendingMove.task.id, { 
      status: 'done', 
      done_note: note || null,
      evidence: evidence || pendingMove.task.evidence,
    });
    setPendingMove(null);
  };

  const handleDoneSkip = async () => {
    if (!pendingMove) return;
    await onUpdateTask(pendingMove.task.id, { status: 'done' });
    setPendingMove(null);
  };

  if (tasks.length === 0) {
    return (
      <p className="text-sm text-muted-foreground/60 text-center py-8 italic">
        No tasks yet. Add your first task to get started.
      </p>
    );
  }

  const SortButton = ({ field, children }: { field: SortField; children: React.ReactNode }) => (
    <Button 
      variant="ghost" 
      size="sm" 
      className="h-auto p-0 hover:bg-transparent font-medium"
      onClick={() => toggleSort(field)}
    >
      {children}
      <ArrowUpDown className={cn(
        "ml-1 h-3 w-3",
        sortField === field ? "opacity-100" : "opacity-30"
      )} />
    </Button>
  );

  return (
    <>
      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[40%]">Task</TableHead>
              <TableHead><SortButton field="status">Status</SortButton></TableHead>
              <TableHead><SortButton field="due_date">Due</SortButton></TableHead>
              <TableHead><SortButton field="priority">Priority</SortButton></TableHead>
              <TableHead><SortButton field="task_type">Type</SortButton></TableHead>
              <TableHead className="w-10"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedTasks.map((task) => {
              const StatusIcon = STATUS_CONFIG[task.status].icon;
              const TypeIcon = TYPE_ICONS[task.task_type] || FileText;
              
              return (
                <TableRow 
                  key={task.id} 
                  className={cn(
                    task.status === 'done' && "opacity-60",
                    onEditTask && "cursor-pointer hover:bg-muted/50"
                  )}
                  onClick={() => onEditTask?.(task)}
                >
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className={cn(
                        "text-sm font-medium",
                        task.status === 'done' && "line-through text-muted-foreground"
                      )}>
                        {task.title}
                      </span>
                      {task.source === 'ai' && (
                        <Sparkles className="h-3 w-3 text-primary/50" />
                      )}
                    </div>
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Select 
                      value={task.status} 
                      onValueChange={(v) => handleStatusChange(task, v as TaskStatus)}
                    >
                      <SelectTrigger className="h-7 w-24 text-xs">
                        <StatusIcon className={cn("h-3 w-3 mr-1", STATUS_CONFIG[task.status].className)} />
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(STATUS_CONFIG).map(([status, config]) => (
                          <SelectItem key={status} value={status} className="text-xs">
                            <div className="flex items-center gap-1">
                              <config.icon className={cn("h-3 w-3", config.className)} />
                              {config.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {task.due_date ? format(new Date(task.due_date), 'MMM d') : '—'}
                  </TableCell>
                  <TableCell>
                    <Badge className={cn("h-5 text-[10px]", {
                      'bg-primary/20 text-primary': task.priority === 'high',
                      'bg-secondary/30 text-secondary-foreground': task.priority === 'medium',
                      'bg-muted/50 text-muted-foreground': task.priority === 'low',
                    })}>
                      {task.priority}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="h-5 text-[10px] gap-1">
                      <TypeIcon className="h-3 w-3" />
                      {task.task_type}
                    </Badge>
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-1">
                      {onEditTask && (
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-6 w-6"
                          onClick={() => onEditTask(task)}
                        >
                          <Pencil className="h-3 w-3 text-muted-foreground" />
                        </Button>
                      )}
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-6 w-6"
                        onClick={() => {
                          if (confirm('Delete this task?')) {
                            onDeleteTask(task.id);
                          }
                        }}
                      >
                        <Trash2 className="h-3 w-3 text-muted-foreground" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <BlockedReasonModal
        open={showBlockedModal}
        onOpenChange={(open) => {
          setShowBlockedModal(open);
          if (!open) setPendingMove(null);
        }}
        onConfirm={handleBlockedConfirm}
        taskTitle={pendingMove?.task.title || ''}
      />

      <DoneNoteModal
        open={showDoneModal}
        onOpenChange={(open) => {
          setShowDoneModal(open);
          if (!open) setPendingMove(null);
        }}
        onConfirm={handleDoneConfirm}
        onSkip={handleDoneSkip}
        taskTitle={pendingMove?.task.title || ''}
      />
    </>
  );
}